import React, { useMemo } from "react";
import type { ChangeEventHandler } from "react";
import classnames from "classnames";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Label,
  FormGroup,
  InputGroup,
  Input,
  FormFeedback,
} from "reactstrap";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Link } from "react-router-dom";
import SimpleHeader from "@/components/Headers/SimpleHeader";
import BlockContentArrayContainer from "../PagesComponent/UpdatePage/BlockContentArrayContainer";
import ImageUploadContainer from "../User/adding-user/ImageUploadContainer";
import BylinePeopleFields from "./BylinePeopleFields";

type UpdatePostViewProps = {
  loading: boolean;
  type: boolean;
  title: string;
  reactQuillText: string;
  validation: { title: boolean; content: boolean };
  blocks: Record<string, unknown>[];
  onToggleType: ChangeEventHandler<HTMLInputElement>;
  onTitleChange: ChangeEventHandler<HTMLInputElement>;
  onQuillChange: (value: string) => void;
  onBlocksAdd: (blocks: Record<string, unknown>[]) => void;
  onBlocksDelete: (blocks: Record<string, unknown>[]) => void;
  onBlocksProcess: (blocks: Record<string, unknown>[]) => void;
  onImageUpload: (img: unknown) => void;
  // UpdatePostContainer `imageForPreview`: the stored URL, a pick's secure_url, or null.
  imagePreview: string | null;
  onUpdateClick: () => void;
  editor: string | null;
  reviewer: string | null;
  onEditorChange: (id: string | null) => void;
  onReviewerChange: (id: string | null) => void;
};

function UpdatePostView({
  loading,
  type,
  title,
  reactQuillText,
  validation,
  blocks,
  onToggleType,
  onTitleChange,
  onQuillChange,
  onBlocksAdd,
  onBlocksDelete,
  onBlocksProcess,
  onImageUpload,
  imagePreview,
  onUpdateClick,
  editor,
  reviewer,
  onEditorChange,
  onReviewerChange,
}: UpdatePostViewProps) {
  // Memoize quill modules for smoother typing
  const quillModules = useMemo(
    () => ({
      toolbar: [
        ["bold", "italic"],
        ["link", "blockquote", "code"],
        [{ list: "ordered" }, { list: "bullet" }],
      ],
    }),
    []
  );

  const isFormValid = !!title?.trim() && !!reactQuillText?.trim();
  const updateDisabled = loading || !isFormValid;

  return (
    <>
      <SimpleHeader name="Manage Post" parentName="Blog" />

      <Container className="mt--6" fluid>
        <Card>
          <CardHeader>
            <h3 className="mb-0">Update Post</h3>
          </CardHeader>

          <CardBody>
            <Row className="mr-3 ml-2">
              <Col>
                {/* TYPE TOGGLE */}
                <FormGroup row>
                  <Label className="form-control-label" md="2" htmlFor="postType">
                    Post Type
                  </Label>
                  <Col md="4" className="d-flex align-items-center">
                    <label
                      className="custom-toggle custom-toggle-danger mr-2 mb-0"
                      htmlFor="postType"
                    >
                      <input
                        id="postType"
                        checked={!!type}
                        onChange={onToggleType}
                        name="type"
                        type="checkbox"
                        role="switch"
                        aria-checked={!!type}
                        aria-label="Toggle post type"
                      />
                      <span
                        className="custom-toggle-slider rounded-circle"
                        data-label-off="POST"
                        data-label-on="NEWS"
                      />
                    </label>
                    <span className="ml-2">{type ? "NEWS" : "POST"}</span>
                  </Col>
                </FormGroup>

                {/* TITLE */}
                <FormGroup row>
                  <Label className="form-control-label" md="2" htmlFor="title">
                    Post Title
                  </Label>
                  <Col md="6">
                    <InputGroup className={classnames("input-group-merge")}>
                      <Input
                        id="title"
                        placeholder="Insert Post Title"
                        name="title"
                        type="text"
                        value={title}
                        onChange={onTitleChange}
                        required
                        invalid={!validation.title}
                      />
                      <FormFeedback>Required field.</FormFeedback>
                    </InputGroup>
                  </Col>
                </FormGroup>

                {/* EDITOR AND REVIEWER (SCRUM-1918) */}
                <BylinePeopleFields
                  editor={editor}
                  reviewer={reviewer}
                  onEditorChange={onEditorChange}
                  onReviewerChange={onReviewerChange}
                />

                {/* CONTENT */}
                <FormGroup row>
                  <Label className="form-control-label" md="2">
                    Content
                  </Label>
                  <Col md="10">
                    <ReactQuill
                      value={reactQuillText}
                      onChange={onQuillChange}
                      theme="snow"
                      modules={quillModules}
                    />
                    {!validation.content && (
                      <div className="mt-1">
                        <small className="text-danger">Required field.</small>
                      </div>
                    )}
                  </Col>
                </FormGroup>

                {/* BLOCKS */}
                <FormGroup row>
                  <Label className="form-control-label" md="2">
                    Blocks
                  </Label>
                  <Col md="10">
                    <BlockContentArrayContainer
                      blocks={blocks}
                      isRequired={false}
                      add={onBlocksAdd}
                      delete={onBlocksDelete}
                      handleBlockArray={onBlocksProcess}
                    />
                  </Col>
                </FormGroup>

                {/* ACTIONS */}
                <FormGroup row>
                  <Col md="12" className="text-right">
                    <Button
                      className="my-0 mr-2"
                      color="secondary"
                      outline
                      tag={Link}
                      to="/admin/posts"
                    >
                      Back To Posts
                    </Button>
                    <Button
                      className="my-0"
                      color="primary"
                      outline
                      type="button"
                      onClick={onUpdateClick}
                      disabled={updateDisabled}
                    >
                      {loading ? "Updating..." : "Update"}
                    </Button>
                  </Col>
                </FormGroup>
              </Col>

              {/* RIGHT SIDEBAR: IMAGE PICKER */}
              <Col className="order-xl-2" xl="4">
                <Card className="card-pricing border-0 text-center mb-4">
                  <CardHeader className="bg-gradient-success">
                    <h4 className="text-uppercase ls-0 text-white py-2 mb-0">
                      Update
                    </h4>
                  </CardHeader>
                  <CardBody className="px-lg-2">
                    <ImageUploadContainer
                      ImageUpload={onImageUpload}
                      image={imagePreview}
                      uniqueId="post-image"
                      withContext
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>
    </>
  );
}

export default UpdatePostView;
